<template>
  <v-tooltip v-if="badge" :disabled="!showTooltip" location="top">
    <template #activator="{ props }">
      <span
        v-bind="props"
        class="featured-badge"
        :style="badgeStyle"
        role="img"
        :aria-label="`Featured Badge: ${badge.name}`"
      >
        <!-- MDI Icon (iconUrl doesn't start with http) -->
        <v-icon
          v-if="badge.iconUrl && !isImageUrl"
          :icon="badge.iconUrl"
          :size="iconSize"
          class="featured-badge__icon"
        />
        <!-- Image URL (starts with http) -->
        <img
          v-else-if="!imageError && badge.iconUrl && isImageUrl"
          :src="badge.iconUrl"
          :alt="badge.name"
          :width="iconSize"
          :height="iconSize"
          class="featured-badge__image"
          @error="handleImageError"
        />
        <!-- Fallback to initials -->
        <span v-else class="featured-badge__fallback" :style="fallbackStyle">
          {{ initials }}
        </span>
      </span>
    </template>
    <div class="featured-badge__tooltip">
      <div class="featured-badge__tooltip-name">{{ badge.name }}</div>
      <div v-if="badge.description" class="featured-badge__tooltip-desc">
        {{ badge.description }}
      </div>
    </div>
  </v-tooltip>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { Badge } from "@/types";

interface Props {
  badge: Badge | null | undefined;
  size?: number;
  showTooltip?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  badge: undefined,
  size: 16,
  showTooltip: true,
});

const imageError = ref(false);

const isImageUrl = computed(() => {
  return props.badge?.iconUrl?.startsWith("http");
});

const iconSize = computed(() => {
  return Math.round(props.size * 0.7);
});

interface BadgeColorScheme {
  primary: string;
  secondary: string;
  glow: string;
}

const getBadgeColors = (badge: Badge): BadgeColorScheme => {
  const name = badge.name.toLowerCase();
  const badgeType = badge.type;

  // Listen count badges - green/teal theme
  if (
    badgeType === "listenCount" ||
    name.includes("listener") ||
    name.includes("audiophile")
  ) {
    return {
      primary: "rgba(29, 185, 84, 0.95)",
      secondary: "rgba(52, 211, 153, 0.9)",
      glow: "rgba(29, 185, 84, 0.4)",
    };
  }

  // Rating count badges - gold/amber theme
  if (
    badgeType === "ratingCount" ||
    name.includes("critic") ||
    name.includes("rater") ||
    name.includes("curator")
  ) {
    return {
      primary: "rgba(245, 158, 11, 0.95)",
      secondary: "rgba(252, 191, 73, 0.9)",
      glow: "rgba(245, 158, 11, 0.4)",
    };
  }

  // Admin badges
  if (name.includes("admin")) {
    return {
      primary: "rgba(239, 68, 68, 0.95)",
      secondary: "rgba(252, 129, 129, 0.9)",
      glow: "rgba(239, 68, 68, 0.4)",
    };
  }

  // Verified badges
  if (name.includes("verified")) {
    return {
      primary: "rgba(59, 130, 246, 0.95)",
      secondary: "rgba(96, 165, 250, 0.9)",
      glow: "rgba(59, 130, 246, 0.4)",
    };
  }

  // Creator badges
  if (name.includes("creator") || name.includes("publisher")) {
    return {
      primary: "rgba(168, 85, 247, 0.95)",
      secondary: "rgba(192, 132, 252, 0.9)",
      glow: "rgba(168, 85, 247, 0.4)",
    };
  }

  // Default purple
  return {
    primary: "rgba(138, 92, 246, 0.95)",
    secondary: "rgba(168, 130, 255, 0.9)",
    glow: "rgba(138, 92, 246, 0.4)",
  };
};

const badgeColors = computed(() => {
  if (!props.badge) {
    return { primary: "", secondary: "", glow: "" };
  }
  return getBadgeColors(props.badge);
});

const badgeStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  minWidth: `${props.size}px`,
  minHeight: `${props.size}px`,
  background: `linear-gradient(135deg, ${badgeColors.value.primary} 0%, ${badgeColors.value.secondary} 100%)`,
  boxShadow: `0 0 6px ${badgeColors.value.glow}`,
}));

const initials = computed(() => {
  if (!props.badge) return "";
  const upperCaseLetters = props.badge.name.match(/[A-Z]/g);
  if (upperCaseLetters && upperCaseLetters.length >= 2) {
    return upperCaseLetters.slice(0, 2).join("");
  }
  return props.badge.name.substring(0, 2).toUpperCase();
});

const fallbackStyle = computed(() => ({
  fontSize: `${props.size * 0.45}px`,
}));

const handleImageError = () => {
  imageError.value = true;
};
</script>

<style scoped lang="scss">
.featured-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.3);
  vertical-align: middle;
  flex-shrink: 0;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }

  &__icon {
    color: white;
    filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 50%;
  }

  &__fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    text-transform: uppercase;
    user-select: none;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  }

  &__tooltip {
    text-align: center;
    max-width: 180px;
    padding: 6px 10px;
  }

  &__tooltip-name {
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 2px;
  }

  &__tooltip-desc {
    font-size: 0.75rem;
    opacity: 0.85;
    line-height: 1.3;
  }
}
</style>
